// Athletes Page Implementation
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    Users,
    Plus,
    Search,
    Mail,
    Phone,
    MoreVertical,
    UserPlus,
    Crown,
    Calendar,
    Loader2,
    X,
    Copy,
    Check,
    MessageCircle,
    QrCode,
    UserX,
    Link2,
    ExternalLink,
} from 'lucide-react';
import { InviteQRModal } from '../../components/InviteQRModal';

interface AthleteData {
    id: string;
    full_name: string;
    email: string;
    phone: string | null;
    avatar_url: string | null;
    created_at: string;
    pairing: {
        id: string;
        status: string;
        is_premium_seat: boolean;
        paired_at: string | null;
        invite_code: string | null;
    };
}

export function AthletesPage() {
    const { tenant } = useAuth();
    const queryClient = useQueryClient();
    const [searchTerm, setSearchTerm] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteName, setInviteName] = useState('');
    const [invitePhone, setInvitePhone] = useState('');
    const [isExternalAthlete, setIsExternalAthlete] = useState(false);
    const [copiedCode, setCopiedCode] = useState(false);
    const [qrModalData, setQrModalData] = useState<{ code: string; name: string } | null>(null);

    // Fetch students
    const { data: students, isLoading } = useQuery({
        queryKey: ['students', tenant?.id],
        queryFn: async () => {
            if (!tenant?.id) return [];

            const { data, error } = await supabase
                .from('student_pairings')
                .select(`
                    id,
                    status,
                    is_premium_seat,
                    paired_at,
                    invited_email,
                    invited_name,
                    invite_code,
                    student:student_id (
                        id,
                        full_name,
                        email,
                        phone,
                        avatar_url,
                        created_at
                    )
                `)
                .eq('tenant_id', tenant.id)
                .neq('status', 'removed')
                .order('created_at', { ascending: false });

            if (error) throw error;

            return (data || []).map((p) => {
                const limitStudent = Array.isArray(p.student) ? p.student[0] : p.student;
                return {
                    id: limitStudent?.id || p.id,
                    full_name: limitStudent?.full_name || p.invited_name || 'Convidado',
                    email: limitStudent?.email || p.invited_email || '',
                    phone: limitStudent?.phone || null,
                    avatar_url: limitStudent?.avatar_url || null,
                    created_at: limitStudent?.created_at || new Date().toISOString(),
                    pairing: {
                        id: p.id,
                        status: p.status,
                        is_premium_seat: p.is_premium_seat,
                        paired_at: p.paired_at,
                        invite_code: p.invite_code,
                    },
                };
            }) as AthleteData[];
        },
        enabled: !!tenant?.id,
    });

    // Generate invite code
    const generateInviteMutation = useMutation({
        mutationFn: async ({ email, name, phone, isExternal }: { email: string; name: string; phone?: string; isExternal?: boolean }) => {
            if (!tenant?.id) throw new Error('No tenant');

            // 1. Check if user exists
            const { data: existingUser } = await supabase
                .from('users')
                .select('id')
                .eq('email', email)
                .maybeSingle();

            const inviteCode = `INV-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
            const expiresAt = new Date();
            expiresAt.setDate(expiresAt.getDate() + 7);

            // 2. Create pairing with status based on external flag
            const { data, error } = await supabase
                .from('student_pairings')
                .insert({
                    tenant_id: tenant.id,
                    personal_id: tenant.owner_id,
                    student_id: existingUser?.id || null,
                    status: isExternal ? 'external' : 'pending',
                    invite_code: inviteCode,
                    invite_expires_at: expiresAt.toISOString(),
                    invited_email: email,
                    invited_name: name,
                    invited_phone: phone || null,
                    notes: isExternal
                        ? `Atleta externo: ${name} (${phone || email}) - Gerenciado sem app`
                        : `Convite enviado para: ${name} (${email})`,
                })
                .select()
                .single();

            if (error) throw error;
            return { ...data, inviteCode };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['students'] });
        },
    });

    // Toggle premium seat
    const togglePremiumMutation = useMutation({
        mutationFn: async ({ pairingId, isPremium }: { pairingId: string; isPremium: boolean }) => {
            const { error } = await supabase
                .from('student_pairings')
                .update({
                    is_premium_seat: isPremium,
                    seat_granted_at: isPremium ? new Date().toISOString() : null,
                })
                .eq('id', pairingId);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['students'] });
        },
    });

    // Remove student
    const removeStudentMutation = useMutation({
        mutationFn: async (pairingId: string) => {
            const { error } = await supabase
                .from('student_pairings')
                .update({
                    status: 'removed',
                    removed_at: new Date().toISOString(),
                })
                .eq('id', pairingId);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['students'] });
        },
    });

    const filteredStudents = students?.filter(
        (s) =>
            s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const activeStudents = filteredStudents?.filter((s) => s.pairing.status === 'active' || s.pairing.status === 'external') || [];
    const pendingStudents = filteredStudents?.filter((s) => s.pairing.status === 'pending') || [];

    const handleInvite = async () => {
        if (!inviteName) return;
        if (!isExternalAthlete && !inviteEmail) return;

        try {
            await generateInviteMutation.mutateAsync({
                email: inviteEmail || `external_${Date.now()}@gofit.app`,
                name: inviteName,
                phone: invitePhone,
                isExternal: isExternalAthlete
            });
            setShowInviteModal(false);
            setInviteEmail('');
            setInviteName('');
            setInvitePhone('');
            setIsExternalAthlete(false);
        } catch (error) {
            console.error('Error creating invite:', error);
        }
    };

    const copyInviteLink = (code: string | null) => {
        if (!code) return;
        navigator.clipboard.writeText(`${window.location.origin}/convite/${code}`);
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
    };

    const shareOnWhatsApp = (code: string | null, name: string) => {
        if (!code) return;
        const link = `${window.location.origin}/convite/${code}`;
        const message = encodeURIComponent(`Olá ${name}! Sou seu Personal Trainer no GoFit. Clique no link para aceitar meu convite e começarmos seus treinos: ${link}`);
        window.open(`https://wa.me/?text=${message}`, '_blank');
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="section-title flex items-center gap-2">
                        <Users className="w-6 h-6" />
                        Meus Atletas
                    </h1>
                    <p className="section-subtitle">
                        {activeStudents.length} atletas ativos
                    </p>
                </div>
                <button
                    onClick={() => setShowInviteModal(true)}
                    className="btn-primary"
                >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Convidar Atleta
                </button>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Buscar por nome ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input pl-10"
                />
            </div>

            {/* Active Students */}
            <div className="space-y-4">
                <h2 className="font-semibold text-gray-900">Atletas Ativos ({activeStudents.length})</h2>

                {activeStudents.length === 0 ? (
                    <div className="card text-center py-12">
                        <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                        <h3 className="font-medium text-gray-900 mb-2">Nenhum atleta ainda</h3>
                        <p className="text-gray-500 mb-4">Convide seus alunos para começar</p>
                        <button
                            onClick={() => setShowInviteModal(true)}
                            className="btn-primary"
                        >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Convidar Primeiro Atleta
                        </button>
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {activeStudents.map((student) => (
                            <AthleteCard
                                key={student.id}
                                student={student}
                                onTogglePremium={() =>
                                    togglePremiumMutation.mutate({
                                        pairingId: student.pairing.id,
                                        isPremium: !student.pairing.is_premium_seat,
                                    })
                                }
                                onRemove={() => {
                                    if (confirm('Tem certeza que deseja remover este atleta?')) {
                                        removeStudentMutation.mutate(student.pairing.id);
                                    }
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Pending Invites */}
            {pendingStudents.length > 0 && (
                <div className="space-y-4">
                    <h2 className="font-semibold text-gray-900">
                        Convites Pendentes ({pendingStudents.length})
                    </h2>
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {pendingStudents.map((athlete) => (
                            <div key={athlete.pairing.id} className="card bg-amber-50/50 border-amber-100">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-amber-900 truncate">
                                            {athlete.full_name}
                                        </p>
                                        <p className="text-sm text-amber-700 truncate">
                                            {athlete.email}
                                        </p>
                                        <div className="flex items-center gap-1 mt-2 text-[10px] font-medium text-amber-600 uppercase tracking-wider">
                                            <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                                            Aguardando aceite
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <button
                                            onClick={() => copyInviteLink(athlete.pairing.invite_code)}
                                            className="p-2 hover:bg-amber-100 rounded-lg text-amber-700 transition-colors"
                                            title="Copiar link de convite"
                                        >
                                            {copiedCode ? (
                                                <Check className="w-4 h-4" />
                                            ) : (
                                                <Copy className="w-4 h-4" />
                                            )}
                                        </button>
                                        <button
                                            onClick={() => athlete.pairing.invite_code && setQrModalData({
                                                code: athlete.pairing.invite_code,
                                                name: athlete.full_name
                                            })}
                                            className="p-2 hover:bg-purple-100 rounded-lg text-purple-600 transition-colors"
                                            title="QR Code"
                                        >
                                            <QrCode className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => shareOnWhatsApp(athlete.pairing.invite_code, athlete.full_name)}
                                            className="p-2 hover:bg-green-100 rounded-lg text-green-600 transition-colors"
                                            title="Convidar via WhatsApp"
                                        >
                                            <MessageCircle className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => {
                                                if (confirm('Deseja cancelar este convite?')) {
                                                    removeStudentMutation.mutate(athlete.pairing.id);
                                                }
                                            }}
                                            className="p-2 hover:bg-red-100 rounded-lg text-red-600 transition-colors"
                                            title="Cancelar convite"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Invite Modal */}
            {showInviteModal && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {isExternalAthlete ? '➕ Adicionar Atleta Externo' : '📧 Convidar Atleta'}
                            </h2>
                            <button
                                onClick={() => {
                                    setShowInviteModal(false);
                                    setIsExternalAthlete(false);
                                }}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Toggle between invite and external */}
                        <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl mb-6">
                            <button
                                onClick={() => setIsExternalAthlete(false)}
                                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${!isExternalAthlete
                                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <Mail className="w-4 h-4 inline mr-2" />
                                Convidar
                            </button>
                            <button
                                onClick={() => setIsExternalAthlete(true)}
                                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${isExternalAthlete
                                    ? 'bg-white dark:bg-gray-700 shadow-sm text-primary-600'
                                    : 'text-gray-500 hover:text-gray-700'}`}
                            >
                                <UserX className="w-4 h-4 inline mr-2" />
                                Externo
                            </button>
                        </div>

                        {isExternalAthlete && (
                            <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-xl border border-amber-200 dark:border-amber-800">
                                <p className="text-sm text-amber-700 dark:text-amber-300">
                                    💡 <strong>Atleta Externo:</strong> Gerencie alunos que não usarão o app.
                                    Você poderá criar treinos e acompanhar manualmente.
                                </p>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="label">Nome do Atleta *</label>
                                <input
                                    type="text"
                                    value={inviteName}
                                    onChange={(e) => setInviteName(e.target.value)}
                                    placeholder="Ex: João Silva"
                                    className="input"
                                    autoFocus
                                />
                            </div>

                            {!isExternalAthlete && (
                                <div>
                                    <label className="label">Email do Atleta *</label>
                                    <input
                                        type="email"
                                        value={inviteEmail}
                                        onChange={(e) => setInviteEmail(e.target.value)}
                                        placeholder="aluno@email.com"
                                        className="input"
                                    />
                                </div>
                            )}

                            <div>
                                <label className="label">
                                    Telefone/WhatsApp {isExternalAthlete ? '*' : '(opcional)'}
                                </label>
                                <input
                                    type="tel"
                                    value={invitePhone}
                                    onChange={(e) => setInvitePhone(e.target.value)}
                                    placeholder="(11) 99999-9999"
                                    className="input"
                                />
                            </div>

                            <button
                                onClick={handleInvite}
                                disabled={
                                    !inviteName ||
                                    (!isExternalAthlete && !inviteEmail) ||
                                    (isExternalAthlete && !invitePhone) ||
                                    generateInviteMutation.isPending
                                }
                                className="btn-primary w-full py-3 text-base"
                            >
                                {generateInviteMutation.isPending ? (
                                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                                ) : isExternalAthlete ? (
                                    <UserPlus className="w-5 h-5 mr-2" />
                                ) : (
                                    <Mail className="w-5 h-5 mr-2" />
                                )}
                                {isExternalAthlete ? 'Adicionar Atleta' : 'Enviar Convite'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* QR Code Modal */}
            {qrModalData && (
                <InviteQRModal
                    isOpen={!!qrModalData}
                    onClose={() => setQrModalData(null)}
                    inviteCode={qrModalData.code}
                    athleteName={qrModalData.name}
                />
            )}
        </div>
    );
}

// Student Card Component
function AthleteCard({
    student,
    onTogglePremium,
    onRemove,
}: {
    student: AthleteData;
    onTogglePremium: () => void;
    onRemove: () => void;
}) {
    const [showMenu, setShowMenu] = useState(false);

    return (
        <div className="card-hover relative">
            <div className="flex items-start gap-4">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-lg">
                    {student.full_name?.charAt(0).toUpperCase() || '?'}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 truncate">
                            {student.full_name}
                        </h3>
                        {student.pairing.is_premium_seat && (
                            <Crown className="w-4 h-4 text-yellow-500" />
                        )}
                    </div>
                    <p className="text-sm text-gray-500 truncate flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {student.email}
                    </p>
                    {student.phone && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {student.phone}
                        </p>
                    )}
                </div>

                {/* Menu */}
                <div className="relative">
                    <button
                        onClick={() => setShowMenu(!showMenu)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                        <MoreVertical className="w-4 h-4" />
                    </button>

                    {showMenu && (
                        <>
                            <div
                                className="fixed inset-0 z-10"
                                onClick={() => setShowMenu(false)}
                            />
                            <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border py-1 z-20 min-w-[160px]">
                                <button
                                    onClick={() => {
                                        onTogglePremium();
                                        setShowMenu(false);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Crown className="w-4 h-4" />
                                    {student.pairing.is_premium_seat
                                        ? 'Remover Premium'
                                        : 'Tornar Premium'}
                                </button>
                                <button
                                    onClick={() => {
                                        onRemove();
                                        setShowMenu(false);
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-red-600 flex items-center gap-2"
                                >
                                    <X className="w-4 h-4" />
                                    Remover Atleta
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Stats */}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between text-sm">
                <div className="flex items-center gap-1 text-gray-500">
                    <Calendar className="w-4 h-4" />
                    <span>
                        Desde{' '}
                        {student.pairing.paired_at
                            ? new Date(student.pairing.paired_at).toLocaleDateString('pt-BR', {
                                month: 'short',
                                year: 'numeric',
                            })
                            : 'N/A'}
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    {student.pairing.status === 'external' && (
                        <span className="badge bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                            🔗 Externo
                        </span>
                    )}
                    <span
                        className={`badge ${student.pairing.is_premium_seat ? 'badge-primary' : 'badge-secondary'
                            }`}
                    >
                        {student.pairing.is_premium_seat ? 'Premium' : 'Free'}
                    </span>
                </div>
            </div>
        </div>
    );
}
